import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Profile.css';

function Profile() {
  const [formData, setFormData] = useState({
    email: '',
    nome: '',
    apelido: '',
    cpf: '',
    telefone: '',
    foto: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/wp-json/palpitex/v1/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFormData({
          email: response.data.email || '',
          nome: response.data.nome || '',
          apelido: response.data.apelido || '',
          cpf: response.data.cpf || '',
          telefone: response.data.telefone || '',
          foto: null,
        });
        if (response.data.foto) {
          setPreviewImage(response.data.foto);
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error.response || error.message);
        setErrors({ general: 'Não foi possível carregar os dados do perfil. Tente novamente mais tarde.' });
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Handlers
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setFormData({ ...formData, [name]: file });
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setPreviewImage(reader.result);
        reader.readAsDataURL(file);
      } else {
        setPreviewImage(formData.foto || null);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const validatePhone = (phone) => {
    return /^\(\d{2}\) 9-\d{4}-\d{4}$/.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome) newErrors.nome = 'Nome é obrigatório';
    if (!formData.apelido) newErrors.apelido = 'Apelido é obrigatório';
    if (!formData.telefone || !validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido (formato: (99) 9-9999-9999)';
    }
    return newErrors;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length >= 3 && value[2] !== '9') value = value.slice(0, 2) + '9' + value.slice(2);
    value = value
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{1})(\d{4})/, '$1-$2')
      .replace(/(\d{4})(\d{4})$/, '$1-$2');
    setFormData({ ...formData, telefone: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    setSuccessMessage('');

    const formDataToSend = new FormData();
    formDataToSend.append('nome', formData.nome);
    formDataToSend.append('apelido', formData.apelido);
    formDataToSend.append('telefone', formData.telefone);
    if (formData.foto) formDataToSend.append('foto', formData.foto);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/wp-json/palpitex/v1/user/update`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update localStorage with new data
      localStorage.setItem('userNome', formData.nome);
      localStorage.setItem('userApelido', formData.apelido);
      localStorage.setItem('userTelefone', formData.telefone);
      if (response.data.foto) {
        localStorage.setItem('userFoto', response.data.foto);
      }

      setSuccessMessage('Perfil atualizado com sucesso!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error.response || error.message);
      setErrors({ general: error.response?.data?.message || 'Erro ao atualizar perfil' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.email) {
    return <div className="form-container">Carregando...</div>;
  }

  return (
    <div className="form-container">
      <h2>Meu Perfil</h2>
      <form onSubmit={handleSubmit}>
        <div>
          <label>E-mail</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            disabled={true}
          />
        </div>
        <div>
          <label>CPF</label>
          <input
            type="text"
            name="cpf"
            value={formData.cpf}
            disabled={true}
          />
        </div>
        <div>
          <label>Nome completo</label>
          <input
            type="text"
            name="nome"
            placeholder="Nome completo"
            value={formData.nome}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.nome && <p className="error">{errors.nome}</p>}
        </div>
        <div>
          <label>Apelido (nome de usuário)</label>
          <input
            type="text"
            name="apelido"
            placeholder="Apelido"
            value={formData.apelido}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.apelido && <p className="error">{errors.apelido}</p>}
        </div>
        <div>
          <label>Telefone</label>
          <input
            type="tel"
            name="telefone"
            placeholder="Telefone (99) 9-9999-9999"
            value={formData.telefone}
            onChange={handlePhoneChange}
            maxLength="15"
            inputMode="numeric"
            disabled={loading}
          />
          {errors.telefone && <p className="error">{errors.telefone}</p>}
        </div>
        <div className="field-group-with-icon">
          <label>Foto de Perfil</label>
          <img
            width="100"
            src={previewImage || `${process.env.PUBLIC_URL}/images/sem-foto.jpeg`}
            alt="Foto Perfil"
            className="foto-perfil"
            onClick={handleImageClick}
            style={{ cursor: 'pointer' }}
          />
          <input
            type="file"
            name="foto"
            accept="image/*"
            onChange={handleChange}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
        </div>
        {errors.general && <p className="error">{errors.general}</p>}
        {successMessage && <p className="success">{successMessage}</p>}
        <button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </form>
    </div>
  );
}

export default Profile;